import { apiConfig } from '../config/apiConfig';
import { refreshTokensIfNeeded, getAuthorizationHeader } from './auth';

export class CreditsError extends Error {
  constructor(status, message, data = {}) {
    super(message);
    this.name = 'CreditsError';
    this.status = status;
    this.data = data;
  }
}

async function creditsFetch(endpoint, { method = 'GET', body = null, params = {} } = {}) {
  await refreshTokensIfNeeded();

  const authHeader = getAuthorizationHeader();
  if (!authHeader) {
    throw new CreditsError(401, 'Not authenticated');
  }

  const baseUrl = apiConfig.creditsApi;
  if (!baseUrl) {
    throw new Error('Credits API endpoint not configured');
  }

  const queryString = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

  const url = `${baseUrl}/${endpoint}${queryString ? `?${queryString}` : ''}`;

  const headers = {
    'Authorization': authHeader,
    'Content-Type': 'application/json',
  };

  const options = { method, headers };
  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (response.status === 401) {
    throw new CreditsError(401, 'Authentication expired. Please sign in again.');
  }

  if (response.status === 402) {
    const data = await response.json();
    throw new CreditsError(402, 'Insufficient credits', data);
  }

  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const data = JSON.parse(text);
      message = data.error || data.message || text;
    } catch (e) {
      // use raw text
    }
    throw new CreditsError(response.status, message || `Request failed with status ${response.status}`);
  }

  return response.json();
}

export async function fetchCreditBalance() {
  return creditsFetch('balance');
}

export async function deductCredits(count) {
  return creditsFetch('deduct', { method: 'POST', body: { count } });
}

export async function createCheckoutSession() {
  return creditsFetch('checkout', { method: 'POST' });
}

export async function verifyCreditPurchase(sessionId) {
  return creditsFetch('success', { params: { session_id: sessionId } });
}
